import { Business as BusinessIcon } from "@mui/icons-material";
import { Box, Link } from "@mui/material";
import {
  AdvancedMarker,
  ControlPosition,
  InfoWindow,
  Map,
  MapControl,
  useApiIsLoaded,
  useMap,
} from "@vis.gl/react-google-maps";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { PriceTag } from "../../admin/products/map/PriceTag";
import { useAuthContext } from "../../auth/AuthContext";
import type { AdminCustomerListItem } from "../customersApi";
import { filterCustomers } from "../list/filterCustomers";
import { useCustomersSearch } from "../list/useCustomersSearch";
import { accountManagerColor } from "./accountManagerColor";
import CustomerMapDetails from "./CustomerMapDetails";
import CustomerMapFilters from "./CustomerMapFilters";
import CustomerMapLegend from "./CustomerMapLegend";
import type { LocatedCustomer } from "./locatedCustomer";
import { isLocated } from "./locatedCustomer";

const centerOfPoland = { lat: 52.0693, lng: 19.4803 };
const singleCustomerZoom = 12;

interface Props {
  readonly customers: AdminCustomerListItem[];
}

const CustomerMap: FC<Props> = ({ customers }) => {
  const { context } = useAuthContext();
  const { customersSearch, setCustomersSearch, clear } = useCustomersSearch(context ?? undefined);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const mapsApiLoaded = useApiIsLoaded();
  const map = useMap();

  const located = useMemo(
    () => filterCustomers(customers, customersSearch).filter(isLocated),
    [customers, customersSearch],
  );
  const selectedCustomer = located.find((customer) => customer.id === selectedCustomerId);

  useEffect(() => {
    if (map && mapsApiLoaded) {
      fitToCustomers(map, located);
    }
  }, [map, mapsApiLoaded, located]);

  return (
    <Box sx={{ position: "absolute", inset: 0, pt: { xs: "4em", md: "8em" } }}>
      <Map
        mapId="CustomerMap"
        style={{ width: "100%", height: "100%" }}
        defaultCenter={centerOfPoland}
        defaultZoom={6}
        gestureHandling="greedy"
        disableDefaultUI
        mapTypeControl
        mapTypeControlOptions={{ position: ControlPosition.TOP_RIGHT }}
        zoomControl
        onClick={() => setSelectedCustomerId(null)}
      >
        <MapControl position={ControlPosition.TOP_LEFT}>
          <Box sx={{ background: "white", p: 1.5, width: 300, maxHeight: "70vh", overflowY: "auto", opacity: 0.9 }}>
            <CustomerMapFilters filters={customersSearch} onChange={setCustomersSearch} onClear={clear} />
            <CustomerMapLegend customers={located} />
          </Box>
        </MapControl>
        {located.map((customer) => (
          <AdvancedMarker
            key={customer.id}
            title={customer.name}
            position={customer.publicContact.location}
            onClick={() => setSelectedCustomerId(customer.id)}
          >
            <PriceTag {...accountManagerColor(customer.accountManager)}>
              <BusinessIcon fontSize="small" />
            </PriceTag>
          </AdvancedMarker>
        ))}
        {selectedCustomer && (
          <InfoWindow
            position={selectedCustomer.publicContact.location}
            onCloseClick={() => setSelectedCustomerId(null)}
            pixelOffset={[0, -35]}
            headerContent={
              <Link component={RouterLink} to={`/customers/${selectedCustomer.id}`} variant="subtitle2">
                {selectedCustomer.name}
              </Link>
            }
          >
            <CustomerMapDetails customer={selectedCustomer} />
          </InfoWindow>
        )}
      </Map>
    </Box>
  );
};

function fitToCustomers(map: google.maps.Map, customers: LocatedCustomer[]) {
  if (customers.length === 0) {
    return;
  }
  if (customers.length === 1) {
    map.setCenter(customers[0].publicContact.location);
    map.setZoom(singleCustomerZoom);
    return;
  }
  const bounds = customers.reduce(
    (result, customer) => result.extend(customer.publicContact.location),
    new google.maps.LatLngBounds(),
  );
  map.fitBounds(bounds);
}

export default CustomerMap;
